import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { BatchAddMemberFormRow } from '../batch-add-members-file-upload/types/batch-add-member-form-row';
import { CreateMemberRequest } from '../../../../models/users/requests/create-member-request';
import { TypeService } from '../../../../services/type/type-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { dateFormatValidator } from '../../../../validators/date-format.validator';
import { BatchAddMembersFormFactory } from '../batch-add-members-file-upload/utils/batch-add-members-form-factory';
import { DateConversionUtils } from '../../../../utils/date-conversion-utils';
import { BatchAddMemberFormGroup } from '../batch-add-members-file-upload/types/batch-add-member-form-group';
import { ButtonTypeEnum } from '../../../../ui/buttons/button/button-type.enum';
import { MemberTableLeaveDetails } from './interfaces/member-table-leave-details';
import { MemberTableEmploymentHistory } from './interfaces/member-table-employment-history';
import { DropDownItem } from '../../../../models/shared/stylesheet/drop-down-item';

@Component({
  selector: 'app-batch-add-members-information-table-card',
  templateUrl: './batch-add-members-information-table-card.component.html',
  styleUrls: ['./batch-add-members-information-table-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchAddMembersInformationTableCardComponent implements OnChanges {
  @Input() rows!: FormArray<BatchAddMemberFormRow>;
  @Input() cardTitle: string = '';
  @Input() description: string = '';
  @Input() primaryButtonText: string = $localize`Continue`;
  @Input() isLoading: boolean = false;

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() tableSubmitted = new EventEmitter<CreateMemberRequest[]>();

  private typeService = inject(TypeService);

  private _allFormsValid = new BehaviorSubject(false);
  public allFormsValid$ = this._allFormsValid as Observable<boolean>;

  private leaveControls = ['leaveType', 'leaveContinuedContributions', 'leaveExpectedReturnDate', 'leaveStartDate'];

  public leaveTypeDropdowns$ = this.typeService.memberEmploymentLeaveTypes$.pipe(
    map(types => {
      return types.map(t => new DropDownItem(t?.name, t?.id));
    })
  );

  public employmentTypeDropdowns$ = this.typeService.memberEmploymentTypes$.pipe(
    map(types => {
      return types.map(t => new DropDownItem(t?.name, t?.id));
    })
  );

  public provinceDropdowns$ = this.typeService.provinces$.pipe(
    map(provinces => provinces.map(province => new DropDownItem(province.name, province.id)))
  );

  public yesNoDropdowns = [new DropDownItem($localize`Yes`, true), new DropDownItem($localize`No`, false)];

  public onLeaveValueChanged(value: boolean, rowIndex: number): void {
    const form = this.rows.at(rowIndex);
    if (!form) return;

    this.leaveControls.forEach(ctrl => {
      const control = form.get(ctrl);
      if (!control) return;

      if (value) {
        control.enable();
        const validators = [Validators.required];
        if (ctrl === 'leaveExpectedReturnDate' || ctrl === 'leaveStartDate') {
          validators.push(dateFormatValidator);
        }
        control.setValidators(validators);
      } else {
        control.patchValue(null);
        control.clearValidators();
        control.disable();
      }

      control.updateValueAndValidity();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows) {
      this.tableValidityChanged();
    }
  }

  public checkContributionStartDate(val: string, rowIndex: number): void {
    const completeDate = this.isMaskedDateComplete(val);
    if (completeDate) {
      const rowData = this.rows.at(rowIndex);
      if (!rowData.value.onLeave) {
        // if user is not on leave
        rowData.submitAttempted = true;
      }
    }
  }

  private isMaskedDateComplete(maskedDate: string | null | undefined): boolean {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return !!maskedDate && maskedDate.trim() !== '' && dateRegex.test(maskedDate);
  }

  private tableValidityChanged(): void {
    const formsAreValid = this.rows.controls.every(row => row.valid);
    this._allFormsValid.next(formsAreValid);
  }

  public addRow(): void {
    this.rows.insert(0, BatchAddMembersFormFactory.initializeMemberFormRow());
    this.tableValidityChanged();
  }

  public removeRow(rowIndex: number): void {
    this.rows.removeAt(rowIndex);
    this.tableValidityChanged();
  }

  public rowHasError(index: number): boolean {
    const form = this.rows.at(index);
    if (!form) return false;
    return !form.valid && !!form.submitAttempted;
  }

  public onRowBlur(event: FocusEvent, rowIndex: number): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const rowElement = event.currentTarget as HTMLElement;
    const row = this.rows.at(rowIndex);
    const rowDeleted = relatedTarget?.classList?.contains('delete-button');
    if (!rowElement.contains(relatedTarget) && !rowDeleted) {
      row.submitAttempted = true;
    }
    this.tableValidityChanged();
  }

  private formatDateForBackend(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const date = DateConversionUtils.shorthandToDate(dateStr);
    return date ? DateConversionUtils.dateToNativeInput(date) : '';
  }

  private getLeaveDetails(row: BatchAddMemberFormGroup): MemberTableLeaveDetails | null {
    if (row.value.onLeave) {
      return {
        leaveTypeId: row.value.leaveType ?? null,
        expectedReturnDate: this.formatDateForBackend(row.value.leaveExpectedReturnDate),
        startDate: this.formatDateForBackend(row.value.leaveStartDate)
      };
    } else {
      return null;
    }
  }

  private getEmploymentHistory(row: BatchAddMemberFormGroup): MemberTableEmploymentHistory {
    return {
      startDate: this.formatDateForBackend(row.value.employmentStartDate),
      onLeave: row.value.onLeave ?? false,
      leaveDetails: this.getLeaveDetails(row),
      employmentTypeId: row.value.employmentType ?? -1,
      workFromHome: row.value.workFromHome ?? false,
      provinceOfEmploymentId: row.value.provinceOfEmployment ?? -1,
      contributionStartDate: this.formatDateForBackend(row.value.contributionStart)
    };
  }

  public submitTable(): void {
    const newMembers = this.rows.controls.map(row => {
      const value = row.getRawValue();
      const employmentHistory = this.getEmploymentHistory(row);

      return Object.assign(new CreateMemberRequest(), {
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        email: value.email.trim(),
        birthDate: this.formatDateForBackend(value.dateOfBirth),
        socialInsuranceNumber: value.socialInsuranceNumber,
        employmentHistory
      });
    });

    this.tableSubmitted.emit(newMembers);
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
